import {
  Button,
  Modal,
  ModalContent,
} from '@heroui/react';
import { Globe } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ModalHeaderStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 24px 16px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: linear-gradient(135deg, #1a4d4d 0%, #0f3333 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fff' : '#1c2b33')};
  margin: 0;
`;

const ModalBodyStyled = styled.div`
  padding: 24px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.5;
  margin: 0;
`;

const LanguageInfo = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#f3f4f6')};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
`;

const LanguageLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin-bottom: 4px;
`;

const LanguageName = styled.div`
  font-size: 1rem;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fff' : '#1c2b33')};
`;

const ModalFooterStyled = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
`;

interface LanguagePreferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  languageName: string;
  isLoading?: boolean;
}

export const LanguagePreferenceModal: FC<LanguagePreferenceModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  languageName,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      backdrop="opaque"
      size="md"
      isDismissable={!isLoading}
      hideCloseButton={isLoading}
      classNames={{
        backdrop: 'bg-black/50',
        base: 'bg-white dark:bg-[#1a1f26] rounded-md shadow-xl',
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeaderStyled>
              <IconWrapper>
                <Globe size={24} className="text-white" />
              </IconWrapper>
              <ModalTitle>
                {t('userProfile.setPreferredLanguage')}
              </ModalTitle>
            </ModalHeaderStyled>

            <ModalBodyStyled>
              <Description>
                {t('userProfile.languagePreferenceDescription')}
              </Description>
              <LanguageInfo>
                <LanguageLabel>{t('userProfile.selectedLanguage')}</LanguageLabel>
                <LanguageName>{languageName}</LanguageName>
              </LanguageInfo>
            </ModalBodyStyled>

            <ModalFooterStyled>
              <Button
                color="default"
                variant="flat"
                onPress={onClose}
                isDisabled={isLoading}
              >
                {t('userProfile.noThanks')}
              </Button>
              <Button
                color="primary"
                onPress={onConfirm}
                isLoading={isLoading}
                className="bg-[#1a4d4d] hover:bg-[#0f3333]"
              >
                {t('userProfile.yesSetAsPreferred')}
              </Button>
            </ModalFooterStyled>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
